'use client';
import React from 'react';
import Link from 'next/link';
import { IOrganization, IUser } from '@/types';

import { Identity } from '@/hooks/useIdentity';
import { Row } from '@/ui/primatives';

import { BaseHeader, HeaderProps } from '../BaseHeader';

const NameSpacePath: React.FC<{ user?: IUser; organization?: IOrganization }> = ({ user, organization }) => {
  const linkPath = user ? `/${user.username}` : `/${organization?.name}`;
  const displayName = user ? user.username : organization?.name;
  return (
    <Row gap={5} alignItems="center">
      <Link className="path hover-link" href={linkPath} data-ph-capture-attribute-resource-type="namespace">
        {displayName}
      </Link>
    </Row>
  );
};

interface HeaderWithOrganization extends HeaderProps {
  organization: IOrganization;
  user?: never;
}

interface HeaderWithUser extends HeaderProps {
  organization?: never;
  user: IUser;
}

type NamespaceHeaderProps = HeaderWithOrganization | HeaderWithUser;

export const NamespaceHeader: React.FC<NamespaceHeaderProps> = ({
  children,
  currentUser,
  user,
  organization,
  variant,
  noUserContent,
  registrationDisabled,
  onLogout,
  ...rest
}) => {
  return (
    <BaseHeader
      currentUser={currentUser}
      contextPath={<NameSpacePath user={user} organization={organization} />}
      variant={variant}
      noUserContent={noUserContent}
      registrationDisabled={registrationDisabled}
      onLogout={onLogout}
      {...rest}
    >
      {children}
    </BaseHeader>
  );
};

const WrappedNamespaceHeader: React.FC<NamespaceHeaderProps> = ({
  children,
  currentUser,
  user,
  organization,
  variant,
  noUserContent,
  registrationDisabled,
  ...rest
}) => {
  const { logout } = Identity.useContainer();
  return user ? (
    <NamespaceHeader
      currentUser={currentUser}
      user={user}
      variant={variant}
      noUserContent={noUserContent}
      registrationDisabled={registrationDisabled}
      onLogout={logout}
      {...rest}
    >
      {children}
    </NamespaceHeader>
  ) : (
    <NamespaceHeader
      currentUser={currentUser}
      organization={organization}
      variant={variant}
      noUserContent={noUserContent}
      registrationDisabled={registrationDisabled}
      onLogout={logout}
      {...rest}
    >
      {children}
    </NamespaceHeader>
  );
};

export default WrappedNamespaceHeader;
